// Tuning constants shared by server simulation and client prediction.
// A value that only one side needs does not belong here. World geometry
// lives in world-plan.ts.

export const DEFAULT_SERVER_PORT = 2567;

/**
 * The Icecast mount for a club is /<clubId>; the client always reaches it
 * same-origin at /stream/<clubId> (Vite proxy in dev, Caddy in prod), which
 * keeps Web Audio free of CORS concerns. The bare mount URL on the stream
 * host is the public, audio-only surface.
 */
export function streamPathForClub(clubId: string): string {
  return `/stream/${clubId}`;
}

/** Server simulation ticks per second. */
export const TICK_RATE = 20;
/** Seconds per simulation tick. */
export const TICK_DELTA_S = 1 / TICK_RATE;
/** How often the server broadcasts state patches, in milliseconds. */
export const PATCH_INTERVAL_MS = 50;
/**
 * Remote avatars are rendered this far in the past so there is always a
 * pair of snapshots to interpolate between (two patch intervals).
 */
export const INTERP_DELAY_MS = 100;

/**
 * Default gait in metres per second (the club/indoor feel is tuned to it).
 * On the surface this is "run"; walk and sprint bracket it below/above.
 */
export const PLAYER_SPEED = 4;
/** Toggled flavor gait (surface only). Crossing the planet: ~2 h 50 m. */
export const WALK_SPEED = 1.6;
/** Hold-shift gait (surface only, no stamina in v0). Crossing: ~34 min. */
export const SPRINT_SPEED = 8;

/** Skiff top speed, m/s — swoop-class: the planet crosses in ~16.5 min. */
export const SKIFF_MAX_SPEED = 16.5;
/** Skiff acceleration, m/s². */
export const SKIFF_ACCEL = 5.5;
/** Skiff braking deceleration, m/s² — stopping is easier than launching. */
export const SKIFF_BRAKE = 11;
/** Capsule radius in metres; also the collision radius. */
export const PLAYER_RADIUS = 0.35;
/** Capsule height in metres, floor to top. */
export const PLAYER_HEIGHT = 1.7;

export const CHAT_MAX_LENGTH = 240;
/** Minimum interval between chat messages from one client. */
export const CHAT_MIN_INTERVAL_MS = 400;
/** Chat history entries kept client-side. */
export const CHAT_LOG_LIMIT = 100;

export const MAX_CLIENTS_PER_ROOM = 40;

/** Axis-aligned box on the floor plane, in metres. */
export interface Aabb {
  readonly minX: number;
  readonly maxX: number;
  readonly minZ: number;
  readonly maxZ: number;
}
